'use client';

import { useState, useEffect } from 'react';
import translationsCz from '../../public/locales/translations-cz.json';
import { trackError } from './gtm';

type TranslationData = Record<string, string>;

const translationsMap: Record<string, TranslationData> = {
  cz: translationsCz as TranslationData,
};

// Client-side hook for component translations
export function useTranslations(locale: string = 'cz') {
  const [translations, setTranslations] = useState<TranslationData>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const loadTranslations = () => {
      try {
        setIsLoading(true);
        const data = translationsMap[locale];
        
        if (!data) {
          const err = new Error(`Translations for locale "${locale}" not found`);
          throw err;
        }
        
        setTranslations(data);
        setError(null);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Unknown error');
        setError(error);
        console.error('Error loading translations:', err);
        
        // Track translation loading error
        if (typeof window !== 'undefined') {
          trackError('translation_missing', error.message, window.location.pathname);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadTranslations();
  }, [locale]);

  const t = (key: string): string => {
    // Direct lookup for flat structure
    if (key in translations) {
      return translations[key];
    }
    
    // If still loading, return empty string silently
    if (isLoading) {
      return '';
    }
    
    // Only warn about missing keys after translations have loaded
    console.warn(`Translation key not found: ${key}`);
    
    // Track missing translation key
    if (typeof window !== 'undefined') {
      trackError('translation_missing', `Missing key: ${key}`, window.location.pathname);
    }
    
    return key;
  };

  return { t, isLoading, error };
}

export default useTranslations;